import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Upload, Loader2, RotateCcw, ArrowRight, Check, AlertCircle, ChevronLeft, ChevronRight,
  Shuffle, List, Volume2, Square, Clock,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import { getDeviceId } from '../lib/deviceId';
import { useVoiceContour } from '../lib/voiceContour';
import { useSpeaker } from '../lib/speak';
import { useRecorder } from '../hooks/useRecorder';
import RecordStage from '../components/brand/RecordStage';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../components/ui/drawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIFFICULTY = {
  Beginner: 'text-emerald-300 bg-emerald-400/10',
  Intermediate: 'text-amber-300 bg-amber-400/10',
  Advanced: 'text-rose-300 bg-rose-400/10',
};

const LENGTHS = [
  { key: 'long', label: '30-second reads' },
  { key: 'short', label: 'Quick lines' },
];

const ANALYZING_LINES = ['Lining your words up with the text…', 'Scoring every sound…', 'Checking stress and rhythm…', 'Writing your feedback…'];

const DifficultyPill = ({ value }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${DIFFICULTY[value] || 'text-white/60 bg-white/5'}`}>{value}</span>
);

const lengthOf = (ex) => ex.length || 'short';

const ExerciseRow = ({ ex, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-2xl border p-3.5 text-left transition-colors ${
      active ? 'border-brand-400/60 bg-brand-500/10' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/15'
    }`}
    aria-pressed={active}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-sm font-semibold text-white">{ex.title}</span>
      {ex.completed && <Check className="h-4 w-4 shrink-0 text-emerald-300" />}
    </div>
    <div className="mt-1.5 flex items-center gap-2 text-[11px] text-white/40">
      <DifficultyPill value={ex.difficulty} />
      <span>{ex.category}</span>
    </div>
  </button>
);

const Practice = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exercises, setExercises] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [lengthFilter, setLengthFilter] = useState('long');
  const [focus, setFocus] = useState('All');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lineIdx, setLineIdx] = useState(0);
  const fileInputRef = useRef(null);
  const contour = useVoiceContour();
  const recorder = useRecorder({ contour, maxSeconds: 90 });
  const speaker = useSpeaker();

  const fetchExercises = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/exercises`);
      setExercises(response.data);
      // ?ex=ID opens that exercise (used by "Try again" and recommendations)
      const wanted = response.data.find((ex) => ex.id === searchParams.get('ex'));
      if (wanted) {
        setLengthFilter(lengthOf(wanted));
        setFocus('All');
        setSelectedId(wanted.id);
      } else if (response.data.some((ex) => ex.category === searchParams.get('focus'))) {
        // ?focus=TH%20sounds opens that focus area (from the Progress page)
        setLengthFilter('long');
        setFocus(searchParams.get('focus'));
      }
    } catch (error) {
      console.error('Error fetching exercises:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExercises(); }, []);

  useEffect(() => {
    if (!isAnalyzing) return undefined;
    setLineIdx(0);
    const id = setInterval(() => setLineIdx((i) => Math.min(i + 1, ANALYZING_LINES.length - 1)), 2200);
    return () => clearInterval(id);
  }, [isAnalyzing]);

  const ofLength = useMemo(() => exercises.filter((ex) => lengthOf(ex) === lengthFilter), [exercises, lengthFilter]);
  const focuses = useMemo(() => ['All', ...new Set(ofLength.map((ex) => ex.category))], [ofLength]);
  const visible = useMemo(
    () => (focus === 'All' ? ofLength : ofLength.filter((ex) => ex.category === focus)),
    [ofLength, focus]
  );

  // Keep a valid selection whenever the filters change.
  useEffect(() => {
    if (visible.length && !visible.some((ex) => ex.id === selectedId)) setSelectedId(visible[0].id);
  }, [visible, selectedId]);

  const selected = exercises.find((ex) => ex.id === selectedId) || null;
  const index = visible.findIndex((ex) => ex.id === selectedId);

  const choose = (id) => {
    if (recorder.status === 'recording' || isAnalyzing) return;
    speaker.stop();
    setSelectedId(id);
    recorder.reset();
    setDrawerOpen(false);
  };
  const step = (d) => visible.length && choose(visible[(index + d + visible.length) % visible.length].id);
  const random = () => {
    if (visible.length < 2) return;
    let next = index;
    while (next === index) next = Math.floor(Math.random() * visible.length);
    choose(visible[next].id);
  };
  const switchLength = (key) => {
    setLengthFilter(key);
    setFocus('All');
  };

  const analyzeAudio = async () => {
    if (!recorder.blob) return;
    setIsAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('audio', recorder.blob, 'recording.wav');
      const response = await axios.post(
        `${API}/analyze-audio?reference_text=${encodeURIComponent(selected.target_phrase)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      localStorage.setItem('analysisResult', JSON.stringify(response.data));
      localStorage.setItem('analysisExercise', JSON.stringify({
        id: selected.id, title: selected.title, category: selected.category, length: lengthOf(selected),
      }));
      await axios.post(`${API}/save-result`, {
        user_id: getDeviceId(),
        exercise_id: selected.id,
        analysis_result: response.data,
      });
      navigate('/results');
    } catch (error) {
      console.error('Error analyzing audio:', error);
      setIsAnalyzing(false);
      let errorMessage = 'We couldn’t analyze that take. Please try again.';
      let errorDetails = '';
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'object') {
          errorMessage = detail.error || errorMessage;
          errorDetails = detail.reason || detail.message || '';
        } else {
          errorMessage = detail;
        }
      }
      toast({
        title: 'Analysis failed',
        description: (
          <div>
            <p className="font-semibold">{errorMessage}</p>
            {errorDetails && <p className="mt-1 text-sm">{errorDetails}</p>}
          </div>
        ),
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="h-10 w-10 animate-spin text-brand-400" />
      </div>
    );
  }

  if (loadError || !exercises.length) {
    return (
      <div className="mx-auto grid min-h-[60vh] max-w-md place-items-center px-5 text-center">
        <div>
          <AlertCircle className="mx-auto h-10 w-10 text-coral-400" />
          <h1 className="mt-4 text-xl font-semibold text-white">{loadError ? 'Couldn’t load exercises' : 'No exercises yet'}</h1>
          <p className="mt-2 text-sm text-white/55">{loadError ? 'Check your connection and try again.' : 'Check back soon.'}</p>
          {loadError && <button type="button" onClick={fetchExercises} className="cz-btn-primary mt-6">Try again</button>}
        </div>
      </div>
    );
  }

  const { status } = recorder;
  const isLong = selected && lengthOf(selected) === 'long';
  const hint = {
    idle: isLong ? 'Tap the mic and read the whole passage at an easy pace' : 'Tap the mic, then read the sentence out loud',
    recording: 'Recording — tap to stop when you’re done',
    processing: 'Preparing your take…',
    ready: 'Got it. Listen back, or analyze it',
  }[status];
  const hearing = speaker.speaking === selected?.target_phrase;
  const locked = status === 'recording' || isAnalyzing;

  const list = (
    <div className="space-y-2">
      {visible.map((ex) => (
        <ExerciseRow key={ex.id} ex={ex} active={ex.id === selectedId} onClick={() => choose(ex.id)} />
      ))}
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-5 pt-8 sm:pt-12">
      <div className="animate-fade-up">
        <p className="cz-eyebrow">Practice</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-[-0.03em] text-white sm:text-5xl">
          Say it <span className="cz-display text-brand-400">out loud.</span>
        </h1>
      </div>

      {/* Filters */}
      <div className="mt-7 flex flex-col gap-3">
        <div className="inline-flex self-start rounded-full border border-white/[0.07] bg-white/[0.03] p-1" role="tablist">
          {LENGTHS.map(({ key, label }) => {
            const n = exercises.filter((ex) => lengthOf(ex) === key).length;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={lengthFilter === key}
                onClick={() => switchLength(key)}
                disabled={locked}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  lengthFilter === key ? 'bg-white/10 text-white' : 'text-white/55 hover:text-white'
                }`}
              >
                {label} <span className="ml-1 text-white/35">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5">
          {focuses.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFocus(f)}
              disabled={locked}
              className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] transition-colors ${
                focus === f ? 'border-brand-400/60 bg-brand-500/15 text-white' : 'border-white/[0.08] text-white/55 hover:text-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
        <aside className="hidden min-w-0 lg:block">
          <p className="mb-3 text-xs font-medium uppercase tracking-[0.18em] text-white/40">{visible.length} exercises</p>
          <div className="max-h-[70vh] overflow-y-auto pr-1">{list}</div>
        </aside>

        {selected && (
          <section className="min-w-0 space-y-4">
            {/* Mobile navigation */}
            <div className="flex items-center gap-2 lg:hidden">
              <button type="button" onClick={() => step(-1)} disabled={locked} className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white/70 disabled:opacity-30" aria-label="Previous exercise">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setDrawerOpen(true)} disabled={locked} className="flex h-10 flex-1 items-center justify-center gap-2 rounded-full border border-white/10 text-sm text-white/75 disabled:opacity-30">
                <List className="h-4 w-4" /> {index + 1} of {visible.length}
              </button>
              <button type="button" onClick={random} disabled={locked} className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white/70 disabled:opacity-30" aria-label="Random exercise">
                <Shuffle className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => step(1)} disabled={locked} className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white/70 disabled:opacity-30" aria-label="Next exercise">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="cz-card p-6 sm:p-8" key={selected.id}>
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-lg font-semibold text-white">{selected.title}</h2>
                <DifficultyPill value={selected.difficulty} />
                {isLong && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] px-2 py-0.5 text-[11px] font-medium text-white/60">
                    <Clock className="h-3 w-3" /> about 30 sec
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-sm text-white/50"><span className="text-white/70">{selected.category}.</span> {selected.description}</p>

              <div className="mt-6 flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/35">Read this</p>
                {speaker.canSpeak && (
                  <button
                    type="button"
                    onClick={() => (hearing ? speaker.stop() : speaker.speak(selected.target_phrase))}
                    disabled={status === 'recording'}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-white/75 transition-colors hover:bg-white/5 disabled:opacity-30"
                  >
                    {hearing ? <><Square className="h-3 w-3 fill-current" /> Stop</> : <><Volume2 className="h-3.5 w-3.5" /> Hear it first</>}
                  </button>
                )}
              </div>
              <p
                className={`mt-3 animate-fade-up text-white ${
                  isLong
                    ? 'text-lg leading-[1.75] sm:text-[1.35rem] sm:leading-[1.7]'
                    : 'text-[1.75rem] font-medium leading-snug tracking-[-0.02em] sm:text-4xl'
                }`}
              >
                {isLong ? selected.target_phrase : `“${selected.target_phrase}”`}
              </p>
            </div>

            <RecordStage contour={contour} recorder={recorder} hint={hint} />

            {status === 'ready' && recorder.url && (
              <div className="cz-card flex flex-col gap-4 p-4 sm:flex-row sm:items-center animate-fade-up">
                <audio src={recorder.url} controls className="h-10 w-full sm:flex-1" />
                <div className="flex gap-2">
                  <button type="button" onClick={recorder.reset} disabled={isAnalyzing} className="cz-btn-ghost flex-1 !px-4 sm:flex-none">
                    <RotateCcw className="h-4 w-4" /> Redo
                  </button>
                  <button type="button" onClick={analyzeAudio} disabled={isAnalyzing} className="cz-btn-primary flex-[2] sm:flex-none" data-testid="analyze-btn">
                    {isAnalyzing ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing</> : <>Analyze <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </div>
              </div>
            )}
            {isAnalyzing && (
              <p className="text-center text-sm text-white/55" aria-live="polite">{ANALYZING_LINES[lineIdx]}</p>
            )}

            {status === 'idle' && (
              <div className="text-center">
                <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={(e) => { recorder.loadFile(e.target.files?.[0]); e.target.value = ''; }} />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 text-sm text-white/45 transition-colors hover:text-white">
                  <Upload className="h-4 w-4" /> or upload a recording (MP3, WAV, M4A)
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[85vh] border-white/10 bg-ink-900">
          <DrawerHeader>
            <DrawerTitle className="text-white">{focus === 'All' ? LENGTHS.find((l) => l.key === lengthFilter).label : focus}</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>{list}</div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Practice;
